/**
 * Маршруты сайта: Data Mode react-router (`createBrowserRouter`).
 *
 * Конструктор шкафа грузится лениво: он тянет three.js, а тот весит больше всего
 * остального приложения. Иначе три мегабайта объёмной графики приезжали бы уже
 * 3D-виджет планировщика по той же причине ленивый внутри редактора.
 */
import { createBrowserRouter, Navigate, redirect, useLocation } from "react-router-dom";
import {
	ConfirmEmailPage,
	LoginPage,
	RegisterPage,
	RequestPasswordResetPage,
	ResetPasswordPage,
} from "@/pages/auth";
import { EditorPage } from "@/pages/editor";
import { HomePage } from "@/pages/home";
import {
	ElevationsLandingPage,
	EstimateLandingPage,
	FloorPlanLandingPage,
	Interior3dLandingPage,
	KitchenLandingPage,
	RoomLandingPage,
	WardrobeLandingPage,
} from "@/pages/scenarios";
import { ProjectsPage } from "@/pages/projects";
import { BlogArticlePage, BlogPage } from "@/pages/blog";
import { UserRole } from "@/api";
import { captureCampaign } from "@/shared/lib/analytics";
import { useAuthStore, ViewMode } from "@/shared/store";
import { routerPaths } from "./routerPaths";
import { App } from "./ui/App";

const protectedRoute = (element: React.ReactNode) => (
	<RequireAuthentication>{element}</RequireAuthentication>
);

const editorLoader =
	(viewMode: ViewMode) =>
	({ request }: { request: Request }) => ({
		projectId: new URL(request.url).searchParams.get("projectId"),
		viewMode,
	});

const RequireAuthentication = ({ children }: { children: React.ReactNode }) => {
	const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
	const location = useLocation();
	if (isAuthenticated) return children;
	return <Navigate to={routerPaths.login} replace state={{ from: location }} />;
};

const RequireAdmin = ({ children }: { children: React.ReactNode }) => {
	const role = useAuthStore((state) => state.role);
	if (role === UserRole.Admin) return children;
	return <Navigate to={routerPaths.home} replace />;
};

captureCampaign(window.location.search);

export const router = createBrowserRouter([
	{
		element: <App />,
		children: [
			{ path: routerPaths.home, element: <HomePage /> },
			{ path: routerPaths.floorPlanLanding, element: <FloorPlanLandingPage /> },
			{ path: routerPaths.kitchenLanding, element: <KitchenLandingPage /> },
			{ path: routerPaths.roomLanding, element: <RoomLandingPage /> },
			{ path: routerPaths.interior3dLanding, element: <Interior3dLandingPage /> },
			{ path: routerPaths.wardrobeLanding, element: <WardrobeLandingPage /> },
			{ path: routerPaths.elevationsLanding, element: <ElevationsLandingPage /> },
			{ path: routerPaths.estimateLanding, element: <EstimateLandingPage /> },
			{ path: routerPaths.emailConfirm, element: <ConfirmEmailPage /> },
			{ path: routerPaths.login, element: <LoginPage /> },
			{ path: routerPaths.passwordReset, element: <RequestPasswordResetPage /> },
			{ path: routerPaths.passwordResetConfirm, element: <ResetPasswordPage /> },
			{ path: routerPaths.register, element: <RegisterPage /> },
			{
				path: routerPaths.planner,
				loader: ({ request }) => {
					const url = new URL(request.url);
					captureCampaign(url.search);
					return redirect(`${routerPaths.editor2d}${url.search}`);
				},
			},
			{ path: routerPaths.editor2d, loader: editorLoader(ViewMode.TwoD), element: <EditorPage /> },
			{
				path: routerPaths.editor3d,
				loader: editorLoader(ViewMode.ThreeD),
				element: <EditorPage />,
			},
			{
				path: routerPaths.editorElevations,
				loader: editorLoader(ViewMode.Elevation),
				element: <EditorPage />,
			},
			{
				path: routerPaths.editorEstimate,
				loader: editorLoader(ViewMode.Estimate),
				element: <EditorPage />,
			},
			{ path: routerPaths.projects, element: protectedRoute(<ProjectsPage />) },
			{
				path: routerPaths.admin,
				lazy: () =>
					import("@/pages/admin").then((module) => ({
						Component: () =>
							protectedRoute(
								<RequireAdmin>
									<module.AdminPage />
								</RequireAdmin>,
							),
					})),
			},
			{ path: routerPaths.blog, element: <BlogPage /> },
			{ path: routerPaths.blogArticle, element: <BlogArticlePage /> },
			{
				path: routerPaths.furnitureGenerator,
				// Лениво: страница тянет three.js, в основной бандл он попадать не должен
				lazy: () =>
					import("@/pages/furniture-constructor").then((m) => ({
						Component: m.FurnitureConstructorPage,
					})),
			},
			// Прежний адрес конструктора шкафа — теперь это раздел конструктора мебели
			{
				path: "/generator/wardrobe",
				element: <Navigate to={routerPaths.furnitureGenerator} replace />,
			},
			// Несуществующий адрес — на главную; replace, чтобы «Назад» не вёл обратно на него
			{ path: "*", element: <Navigate to={routerPaths.home} replace /> },
		],
	},
]);
